// ============================================================================
// Corpse State - Client-side corpse entity management
// ============================================================================

import {
  type CauseOfDeathValue,
  EntityType,
  type RotStageValue,
  TILE_SIZE,
  toCauseOfDeath,
  toRotStage,
} from '@dreamgame/shared';
import { computed, signal } from '@preact/signals';

// ============================================================================
// Types
// ============================================================================

// Corpse spawn data from server (uses plain numbers for serialization)
export interface CorpseSpawnData {
  readonly networkId: number;
  readonly x: number;
  readonly y: number;
  readonly originalEntityType: number;
  readonly originalSubType: number;
  readonly originalOwnerId: number;
  readonly name: string | null;
  readonly killerName: string | null;
  readonly causeOfDeath: number;
  readonly deathTick: number;
  readonly rotProgress: number;
  readonly rotStage: number;
  readonly meatYield: number;
  readonly leatherYield: number;
}

// Client-side corpse data
export interface CorpseData {
  readonly networkId: number;
  readonly x: number;
  readonly y: number;

  // Identity
  readonly originalEntityType: number; // EntityType.PAWN or ANIMAL
  readonly originalSubType: number; // NpcTypeValue or 0 for pawns
  readonly originalOwnerId: number;

  // Pawn identity (null for animals)
  readonly name: string | null;

  // Death context
  readonly killerName: string | null;
  readonly causeOfDeath: CauseOfDeathValue;
  readonly deathTick: number;

  // Decay state
  readonly rotProgress: number;
  readonly rotStage: RotStageValue;

  // Resources (animals only)
  readonly meatYield: number;
  readonly leatherYield: number;
}

// ============================================================================
// State
// ============================================================================

export const corpses = signal<Map<number, CorpseData>>(new Map());

// ============================================================================
// Derived State
// ============================================================================

export const allCorpses = computed(() => Array.from(corpses.value.values()));

// Pawn corpses (for burial/mourning UI)
export const pawnCorpses = computed(() =>
  allCorpses.value.filter((c) => c.originalEntityType === EntityType.PAWN)
);

// Animal corpses (for butchering)
export const animalCorpses = computed(() =>
  allCorpses.value.filter((c) => c.originalEntityType === EntityType.ANIMAL)
);

// ============================================================================
// Actions
// ============================================================================

export const addCorpse = ({ snapshot }: { snapshot: CorpseSpawnData }): void => {
  const next = new Map(corpses.value);
  const corpseData: CorpseData = {
    networkId: snapshot.networkId,
    x: snapshot.x,
    y: snapshot.y,
    originalEntityType: snapshot.originalEntityType,
    originalSubType: snapshot.originalSubType,
    originalOwnerId: snapshot.originalOwnerId,
    name: snapshot.name,
    killerName: snapshot.killerName,
    causeOfDeath: toCauseOfDeath(snapshot.causeOfDeath),
    deathTick: snapshot.deathTick,
    rotProgress: snapshot.rotProgress,
    rotStage: toRotStage(snapshot.rotStage),
    meatYield: snapshot.meatYield,
    leatherYield: snapshot.leatherYield,
  };
  next.set(snapshot.networkId, corpseData);
  corpses.value = next;
};

export const removeCorpse = ({ networkId }: { networkId: number }): void => {
  const next = new Map(corpses.value);
  next.delete(networkId);
  corpses.value = next;
};

export const updateCorpse = ({
  networkId,
  updates,
}: {
  networkId: number;
  updates: Partial<CorpseData>;
}): void => {
  const corpse = corpses.value.get(networkId);
  if (!corpse) {
    return;
  }

  const next = new Map(corpses.value);
  next.set(networkId, { ...corpse, ...updates });
  corpses.value = next;
};

// ============================================================================
// Queries
// ============================================================================

export const getCorpse = ({ networkId }: { networkId: number }): CorpseData | undefined => {
  return corpses.value.get(networkId);
};

export const getCorpseAtPosition = ({
  worldX,
  worldY,
}: {
  worldX: number;
  worldY: number;
}): CorpseData | null => {
  const tileX = Math.floor(worldX / TILE_SIZE);
  const tileY = Math.floor(worldY / TILE_SIZE);

  for (const corpse of corpses.value.values()) {
    if (Math.floor(corpse.x) === tileX && Math.floor(corpse.y) === tileY) {
      return corpse;
    }
  }

  return null;
};

export const getCorpsesAtTile = ({
  tileX,
  tileY,
}: {
  tileX: number;
  tileY: number;
}): ReadonlyArray<CorpseData> => {
  const result: CorpseData[] = [];
  for (const corpse of corpses.value.values()) {
    if (Math.floor(corpse.x) === tileX && Math.floor(corpse.y) === tileY) {
      result.push(corpse);
    }
  }
  return result;
};

export const clearCorpses = (): void => {
  corpses.value = new Map();
};
